import { checkoutErrorMessage, isUpgradeResumeRequired } from './actions/billingCheckoutErrorMessages';
import type { PrototypeState } from "./controllerState";
import { matchCall } from "./domUtils";
import { callBillingProvider } from "./prototypeActions";
import type { BillingCycle, PlanCode, PrototypeRuntime, Root } from "./prototypeTypes";
import { closeModal, openModal } from "./navigationController";
import { refreshHomeState } from "./homeStateController";
import { entitlementRefresh, setEntitlementRefreshRuntime } from "./entitlementRefresh/entitlementRefresh";
import {
	mountSubscriptionChooser,
	setSubscriptionChooserError,
	setSubscriptionChooserLoading,
	setSubscriptionChooserPurchasesUnavailable,
	subscriptionChooserContextFromState,
	type SubscriptionPlan,
} from './subscriptionChooserController';
import type { ChooserSource } from './subscriptionChooserState';
import { recordRuntimeError } from './prototypeRuntimeSupport';
import { readCurrentSubscription } from './currentSubscriptionReader';
import type { CurrentSubscription } from './currentSubscriptionReader';
import { planStore } from './planStore';
import { resetTransientSurfaceState } from './transientSurfaceState';
import { reconcilePaywallBillingState, type PaywallIdentitySnapshot } from './paywallBillingStateReconciler';
import { ensureBusinessOverlayMounted } from './businessOverlayLifecycle';
import { lowestPaidPlanWithCapability } from '../../domain/access/lowestPaidPlanWithCapability';

// 防双击: 1 秒时间窗防抖。新订阅重定向 Creem 付款页，付费升级走 Creem API
// 立即扣款，双击/重复点击可能各触发一次支付动作；时间窗在点击即生效，
// 不等 API 返回，避免慢网络下二次点击穿透。服务端另有 30 次/10 分钟限流兜底。
const upgradeClickDebounceMs = 1_000;
// 防双击按 state 实例计时: SPA 中 state 是单例, 1 秒窗口在两次点击间生效;
// 单元测试每个用例新建 state, 时间戳互不泄漏, 用例间不互相吞掉首次点击。
const lastUpgradeClickAt = new WeakMap<PrototypeState, number>();
const paywallRequestGeneration = new WeakMap<Root, number>();
type DynamicRequiredCapability = 'githubDeepDive';
type PaywallOpenOptions = {
	requiredPlan?: SubscriptionPlan;
	dynamicRequiredCapability?: DynamicRequiredCapability;
	setIntent?: (root: Root, state: PrototypeState, requiredPlan?: SubscriptionPlan) => void;
};
const paywallOpenOptions = new WeakMap<Root, PaywallOpenOptions>();

/** Outcome of a previewUpgrade attempt. `ok: true` = the flow reached a terminal
 * hand-off (charge submitted / checkout redirect) so the caller should dismiss
 * the confirm modal; `ok: false, keepOpen` = a secondary prompt (resume
 * auto-renew) was opened on top, the confirm modal must stay open underneath;
 * `ok: false, message` = recoverable failure, the caller keeps the modal open
 * and surfaces `message` inline. */
export type UpgradeAttemptResult =
	| { ok: true }
	| { ok: false; message: string }
	| { ok: false; keepOpen: true };

/**
 * Open the paywall modal with the current viewer’s plan context already
 * applied (Free card hidden for paid users, paywall intent note set by
 * the caller). Centralises the open + paint sequence so every gate
 * path renders a consistent layout.
 */
export function openPaywallWithLayout(
  root: Root,
  state: PrototypeState,
  options: PaywallOpenOptions = {},
): void {
	ensureBusinessOverlayMounted(root, 'paywallModal');
	const paywall = root.querySelector<HTMLElement>('#paywallModal');
	if (paywall) resetTransientSurfaceState(paywall);
	const note = root.querySelector<HTMLElement>('#paywallIntentNote');
	if (note) {
		note.textContent = '';
		note.style.display = 'none';
	}
	options.setIntent?.(root, state, options.requiredPlan);
	paintPaywallRequiredPlan(root, options.requiredPlan);
	openModal(root, 'paywallModal');
	paywallOpenOptions.set(root, options);
	if (state.currentPlan === 'visitor') {
		const initialContext = subscriptionChooserContextFromState(state, { source: 'paywall', requiredPlan: options.requiredPlan });
		if (initialContext) mountSubscriptionChooser(root, { prefix: 'pw', context: initialContext })?.reset(initialContext);
		return;
	}
	loadPaywallSubscription(root, state, options);
}

/** Recover an errored paywall from a fresh document; non-error callers may still request a fresh subscription generation. */
export function retryPaywallSubscription(
	root: Root,
	state: PrototypeState,
	reloadPage: () => void = () => window.location.reload(),
): void {
	if (!root.querySelector<HTMLElement>('#paywallModal')?.classList.contains('active')) return;
	if (root.querySelector<HTMLElement>('[data-subscription-surface="pw"]')?.dataset.subscriptionState === 'error') {
		reloadPage();
		return;
	}
	loadPaywallSubscription(root, state, paywallOpenOptions.get(root) ?? {});
}

function loadPaywallSubscription(root: Root, state: PrototypeState, options: PaywallOpenOptions): void {
	const generation = (paywallRequestGeneration.get(root) ?? 0) + 1;
	paywallRequestGeneration.set(root, generation);
	const identity = capturePaywallIdentity(state, generation);
	setSubscriptionChooserLoading(root, 'pw');
	if (!identity) {
		showPaywallSubscriptionError(root, state, generation, new Error('billing_identity_unavailable'));
		return;
	}
	const request = readCurrentSubscription(state.runtime.api, { force: true });
	if (!request) {
		showPaywallSubscriptionError(root, state, generation, new Error('billing_subscription_reader_unavailable'));
		return;
	}
	void request.then(async ({ subscription }) => {
		if (!ownsPaywallIdentity(root, state, identity)) return;
		if (!validSubscriptionFact(subscription)) {
			showPaywallSubscriptionError(root, state, generation, new Error('billing_subscription_fact_invalid'));
			return;
		}
		// The subscription fact is authoritative over an old bootstrap plan.
		// Reconcile it before any catalog fallback can consult state.currentPlan,
		// and before deciding whether a dynamic entitlement can dismiss the wall.
		const reconciliation = await reconcilePaywallBillingState(
			root,
			state,
			subscription,
			identity,
			() => ownsPaywallIdentity(root, state, identity),
		);
		if (reconciliation === 'stale') return;
		if (reconciliation === 'error') {
			if (!ownsPaywallIdentity(root, state, identity)) return;
			showPaywallSubscriptionError(root, state, generation, new Error('billing_subscription_fact_invalid'));
			return;
		}
		if (!ownsPaywallIdentity(root, state, identity)) return;
		try {
			await ensurePaywallPricingCatalog(state, options.dynamicRequiredCapability === 'githubDeepDive');
		} catch (error) {
			if (ownsPaywallIdentity(root, state, identity)) {
				recordRuntimeError(state.runtime, error);
				setSubscriptionChooserPurchasesUnavailable(root, 'pw');
			}
			return;
		}
		if (!ownsPaywallIdentity(root, state, identity)) return;
		const requiredPlan = resolveRequiredPlanAfterCatalogRefresh(options);
		if (options.dynamicRequiredCapability && viewerHasDynamicCapability(state, options.dynamicRequiredCapability)) {
			// Reconciliation may have corrected a stale paid client state. Only
			// then may a fresh catalog dismiss this dynamic entitlement gate.
			closeModal(root, 'paywallModal');
			return;
		}
		paintPaywallRequiredPlan(root, requiredPlan);
		if (options.dynamicRequiredCapability) options.setIntent?.(root, state, requiredPlan);
		paywallOpenOptions.set(root, { ...options, requiredPlan });
		const context = subscriptionChooserContextFromState(state, { source: 'paywall', requiredPlan, subscription });
		if (!context) {
			showPaywallSubscriptionError(root, state, generation, new Error('billing_subscription_context_invalid'));
			return;
		}
		mountSubscriptionChooser(root, { prefix: 'pw', context })?.reset(context);
	}).catch((error) => {
		if (ownsPaywallIdentity(root, state, identity)) showPaywallSubscriptionError(root, state, generation, error);
	});
}

function resolveRequiredPlanAfterCatalogRefresh(options: PaywallOpenOptions): SubscriptionPlan | undefined {
	if (options.dynamicRequiredCapability !== 'githubDeepDive') return options.requiredPlan;
	const catalog = planStore.getAll();
	return catalog ? lowestPaidPlanWithCapability(catalog, 'githubDeepDive') ?? undefined : undefined;
}

function viewerHasDynamicCapability(state: PrototypeState, capability: DynamicRequiredCapability): boolean {
	return planStore.getPlanEntitlements(state.effectiveAccessPlan)[capability] === true;
}

function capturePaywallIdentity(state: PrototypeState, generation: number): PaywallIdentitySnapshot | null {
	if (!state.isLoggedIn || !state.runtime.userId || !state.runtime.sessionHash) return null;
	return { generation, isLoggedIn: true, userId: state.runtime.userId, sessionHash: state.runtime.sessionHash };
}

function ownsPaywallIdentity(root: Root, state: PrototypeState, identity: PaywallIdentitySnapshot): boolean {
	return isCurrentPaywallRequest(root, identity.generation)
		&& state.isLoggedIn === identity.isLoggedIn
		&& state.runtime.userId === identity.userId
		&& state.runtime.sessionHash === identity.sessionHash;
}

function validSubscriptionFact(subscription: CurrentSubscription | null): boolean {
	if (subscription === null) return true;
	return (subscription.plan === 'lite' || subscription.plan === 'pro' || subscription.plan === 'max')
		&& (subscription.billingCycle === 'monthly' || subscription.billingCycle === 'quarterly' || subscription.billingCycle === 'annual')
		&& typeof subscription.providerSubscriptionId === 'string'
		&& subscription.providerSubscriptionId.trim().length > 0;
}

/** The paywall never paints a paid member’s chooser from an absent or partial
 * catalog. Bootstrap may still be hydrating when the modal opens, so fetch the
 * catalog only at that point and keep the structural state until it is usable. */
async function ensurePaywallPricingCatalog(state: PrototypeState, forceRefresh = false): Promise<void> {
	if (!forceRefresh && hasSaleablePaidPlanCatalog(planStore.getAll()) && !planStore.isExpired()) return;
	const plansReader = state.runtime.api.plans;
	const bootstrapReader = state.runtime.api.bootstrap;
	if (typeof plansReader !== 'function' && typeof bootstrapReader !== 'function') throw new Error('billing_plan_catalog_reader_unavailable');
	const payload = typeof plansReader === 'function'
		? await plansReader.call(state.runtime.api)
		: await bootstrapReader.call(state.runtime.api, state.currentPlan);
	const plans = readSaleablePaidPlanCatalog(payload);
	if (!plans) throw new Error('billing_plan_catalog_invalid');
	const expiresAt = payload && typeof payload === 'object' && !Array.isArray(payload)
		? (payload as Record<string, unknown>).catalogExpiresAt
		: null;
	planStore.setPlans(plans as never, { catalogExpiresAt: typeof expiresAt === 'string' ? expiresAt : null });
}

function hasSaleablePaidPlanCatalog(catalog: Record<string, unknown> | null): boolean {
	return ['lite', 'pro', 'max'].every((code) => hasSaleablePlanPrices(catalog?.[code]));
}

function readSaleablePaidPlanCatalog(payload: unknown): Record<string, unknown> | Array<Record<string, unknown>> | null {
	if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
	const plans = (payload as Record<string, unknown>).plans;
	if (Array.isArray(plans)) {
		const byCode: Record<string, unknown> = {};
		for (const plan of plans) {
			if (!plan || typeof plan !== 'object' || Array.isArray(plan)) continue;
			const code = (plan as Record<string, unknown>).code;
			if (typeof code === 'string') byCode[code] = plan;
		}
		if (!hasSaleablePaidPlanCatalog(byCode)) return null;
		return plans as Array<Record<string, unknown>>;
	}
	if (!plans || typeof plans !== 'object') return null;
	return hasSaleablePaidPlanCatalog(plans as Record<string, unknown>) ? plans as Record<string, unknown> : null;
}

function hasSaleablePlanPrices(plan: unknown): boolean {
	if (!plan || typeof plan !== 'object' || Array.isArray(plan)) return false;
	const record = plan as Record<string, unknown>;
	return ['monthlyCents', 'quarterlyCents', 'annualCents'].every((field) => typeof record[field] === 'number' && Number.isFinite(record[field]) && record[field] > 0);
}

function showPaywallSubscriptionError(root: Root, state: PrototypeState, generation: number, error: unknown): void {
	if (!isCurrentPaywallRequest(root, generation)) return;
	recordRuntimeError(state.runtime, error);
	setSubscriptionChooserError(root, 'pw');
}

function isCurrentPaywallRequest(root: Root, generation: number): boolean {
	return paywallRequestGeneration.get(root) === generation
		&& root.querySelector<HTMLElement>('#paywallModal')?.classList.contains('active') === true;
}

/** The active paywall intent changes whenever its subscription facts reload. */
export function currentPaywallRequestGeneration(root: Root): number | undefined {
	return paywallRequestGeneration.get(root);
}

function paintPaywallRequiredPlan(root: Root, requiredPlan?: SubscriptionPlan): void {
	const modal = root.querySelector<HTMLElement>('#paywallModal');
	const target = root.querySelector<HTMLElement>('#paywallTargetPlan');
	if (!requiredPlan) {
		delete modal?.dataset.requiredPlan;
		if (target) target.textContent = '';
		return;
	}
	if (modal) modal.dataset.requiredPlan = requiredPlan;
	if (target) target.textContent = requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1);
	const note = root.querySelector<HTMLElement>('#paywallIntentNote');
	if (note) {
		note.textContent = `This content requires ${requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)} or higher.`;
		note.style.display = '';
	}
}

/**
 * Every pricing surface resolves its default from the current saleable plan
 * catalog. This prevents a stale previous selection from reaching checkout.
 */
export async function previewUpgrade(
  nextPlan: PlanCode,
  root: Root,
  state: PrototypeState,
  recordRuntimeError: (runtime: PrototypeRuntime, error: unknown) => void,
	options: { providerSubscriptionId?: string; source?: ChooserSource; billingCycle?: BillingCycle; canWriteUi?: () => boolean } = {},
): Promise<UpgradeAttemptResult> {
  // Hard guard: never run the upgrade flow when the target plan is
  // the same as the user's current plan. Catches the historical
  // "Subscribe Free" → previewUpgrade(free→free) footgun and any
  // same-plan click on a paywall tier card.
	if (nextPlan === state.currentPlan && !options.billingCycle) return { ok: true };
	if (nextPlan === 'visitor' || nextPlan === 'free') return { ok: true };
  // 防双击: 1 秒内重复点击忽略(按 state 实例计时)
  const now = Date.now();
  const lastClickAt = lastUpgradeClickAt.get(state) ?? 0;
  if (now - lastClickAt < upgradeClickDebounceMs) return { ok: true };
  lastUpgradeClickAt.set(state, now);
	const targetBillingCycle = options.billingCycle ?? state.runtime.billingCycle;
	const canWriteUi = options.canWriteUi ?? (() => true);
	try {
		let providerSubscriptionId = options.providerSubscriptionId;
		if (state.currentPlan !== 'visitor' && state.currentPlan !== 'free') {
			if (!providerSubscriptionId) {
				const preview = await state.runtime.api.previewUpgrade({
					toPlan: nextPlan,
					billingCycle: targetBillingCycle,
				}) as { subscriptionId?: unknown };
				providerSubscriptionId = typeof preview.subscriptionId === 'string' ? preview.subscriptionId : undefined;
			}
			if (!providerSubscriptionId) throw new Error('billing_subscription_not_loaded');
		}
		const providerResult = await callBillingProvider(state.runtime, state.currentPlan, nextPlan, providerSubscriptionId, targetBillingCycle);
    // P0-1: 不做乐观更新(异步操作等 webhook)。checkout 跳转 Creem, upgrade 启动轮询。
    // 终态由 webhook 驱动 session 刷新后回填(见 entitlementRefresh 模块)。
    if (state.currentPlan === "visitor" || state.currentPlan === "free") {
      // checkout: callBillingProvider 内部已 window.location.assign(checkoutUrl) 跳转, 回站后 refreshSession
    } else {
      // upgrade: 启动短轮询等 webhook 确认
      setEntitlementRefreshRuntime(state.runtime);
			const requestId = providerResult && typeof providerResult === 'object' && typeof (providerResult as Record<string, unknown>).requestId === 'string'
				? String((providerResult as Record<string, unknown>).requestId)
				: undefined;
			entitlementRefresh.startPendingPoll(state.runtime, nextPlan, targetBillingCycle, providerSubscriptionId!, requestId);
			if (canWriteUi()) closeModal(root, "paywallModal");
    }
		return { ok: true };
	} catch (error) {
    recordRuntimeError(state.runtime, error);
	if (isUpgradeResumeRequired(error)) {
			const { presentUpgradeResumePrompt } = await import('./actions/billingUpgradeResumePrompt');
			if (!canWriteUi()) return { ok: false, message: checkoutErrorMessage(error, targetBillingCycle) };
			presentUpgradeResumePrompt(root, nextPlan);
			// Keep the confirm modal open underneath so it reappears once the
			// user re-enables auto-renew; the confirm flow must not treat the
			// resume prompt as a terminal hand-off (that would close step 2).
			return { ok: false, keepOpen: true };
		}
		const message = checkoutErrorMessage(error, targetBillingCycle);
		return { ok: false, message };
	}
}


export function matchBillingCycle(action: string): BillingCycle | null {
	const matched = matchCall(action, 'setSubscriptionBillingCycle');
  if (matched === "monthly" || matched === "quarterly" || matched === "annual") return matched;
  return null;
}

export function toPlanCode(value: string | null): PlanCode | null {
  if (value === "visitor" || value === "free" || value === "lite" || value === "pro" || value === "max") return value;
  return null;
}
